import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaUser, FaUserTie, FaBaby, FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import api from '../services/apiService';
import './NewRegisterModal.scss';

const NewRegisterModal = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [userType, setUserType] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');
  const [clubName, setClubName] = useState('');
  const [isAcademy, setIsAcademy] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  // Player-specific fields
  const [playerClubType, setPlayerClubType] = useState(''); // 'organizer' or 'team'
  // Password fields (used by players, parents, and coaches)
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Coach-specific fields
  const [leagueName, setLeagueName] = useState('');
  // Parent-specific fields
  const [childFirstName, setChildFirstName] = useState('');
  const [childLastName, setChildLastName] = useState('');
  const [coachName, setCoachName] = useState('');
  const [coachPhone, setCoachPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [displayStep, setDisplayStep] = useState(1); // For breadcrumb animation
  const [userExists, setUserExists] = useState(false);
  const [userExistsMessage, setUserExistsMessage] = useState('');
  const [canContinue, setCanContinue] = useState(false);
  const [existingTeams, setExistingTeams] = useState([]);

  const ageGroups = ['U7s', 'U8s', 'U9s', 'U10s', 'U11s', 'U12s', 'U13s', 'U14s', 'U15s', 'U16s', 'U17s', 'U18s', 'U21s', 'U23s'];

  // Phone number validation function
  const validatePhoneNumber = (phone) => {
    if (!phone || !phone.trim()) {
      return { isValid: false, message: 'Mobile number is required' };
    }
    
    const phoneValue = phone.trim();
    
    // Check if phone starts with + or 0
    if (!phoneValue.startsWith('+') && !phoneValue.startsWith('0')) {
      return { isValid: false, message: 'Mobile number must start with + country code or 0' };
    }
    
    // Basic format validation - allows +country code or 0 followed by 10-15 digits
    const phoneRegex = /^(\+\d{1,3}|0)\d{10,15}$/;
    if (!phoneRegex.test(phoneValue)) {
      return { isValid: false, message: 'Please enter a valid mobile number format' };
    }
    
    return { isValid: true, message: '' };
  };

  // Validation functions
  const validateStep2 = () => {
    const newErrors = {};
    
    if (userType === 'parent') {
      if (!firstName.trim()) newErrors.firstName = 'First name is required';
      if (!lastName.trim()) newErrors.lastName = 'Last name is required';
      const phoneValidation = validatePhoneNumber(mobile);
      if (!phoneValidation.isValid) {
        newErrors.mobile = phoneValidation.message;
      }
    } else {
      if (!firstName.trim()) newErrors.firstName = 'First name is required';
      if (!lastName.trim()) newErrors.lastName = 'Last name is required';
      const phoneValidation = validatePhoneNumber(mobile);
      if (!phoneValidation.isValid) {
        newErrors.mobile = phoneValidation.message;
      }
    }
    
    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const validateStep3 = () => {
    const newErrors = {};
    
    if (userType === 'parent') {
      if (!childFirstName.trim()) newErrors.childFirstName = "Child's first name is required";
      if (!childLastName.trim()) newErrors.childLastName = "Child's last name is required";
    } else if (userType === 'coach') {
      // Step 3 for coach: Club Name only
      if (!clubName.trim()) {
        newErrors.clubName = 'Club name is required';
      }
    } else if (userType === 'player') {
      // Step 3 for player: Club Type Question
      if (!playerClubType) {
        newErrors.playerClubType = 'Please select a club type';
      }
    } else {
      // Player: Club Name and Academy (legacy - should not be reached)
      if (!clubName.trim()) {
        newErrors.clubName = 'Club name is required';
      }
      
      if (isAcademy === 'yes' && !ageGroup) {
        newErrors.ageGroup = 'Please select an age group';
      }
    }
    
    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const validateStep4 = () => {
    const newErrors = {};
    
    if (userType === 'coach') {
      // Step 4 for coach: Youth vs adult (maps to academy vs squad)
      if (!isAcademy) {
        newErrors.isAcademy = 'Please choose youth team or adult squad';
      } else if (isAcademy === 'yes' && !ageGroup) {
        newErrors.ageGroup = 'Please select an age group';
      }
    } else if (userType === 'player' && playerClubType === 'organizer') {
      // Step 4 for organizer: Team Name only
      if (!clubName.trim()) {
        newErrors.clubName = 'Team name is required';
      }
    } else if (userType === 'player' && playerClubType === 'team') {
      // Step 4 for team player: Team Information
      if (!clubName.trim()) {
        newErrors.clubName = 'Team name is required';
      }
      
      if (!isAcademy) {
        newErrors.isAcademy = 'Please choose youth team or adult squad';
      } else if (isAcademy === 'yes' && !ageGroup) {
        newErrors.ageGroup = 'Please select an age group';
      }
      
      // Manager/Coach details required for team players
      if (!coachName.trim()) {
        newErrors.coachName = 'Manager/Coach name is required';
      }
      const coachPhoneValidation = validatePhoneNumber(coachPhone);
      if (!coachPhoneValidation.isValid) {
        newErrors.coachPhone = coachPhoneValidation.message;
      }
    } else if (userType === 'parent') {
      // Step 4 for parent: Club Details
      if (!coachName.trim()) newErrors.coachName = 'Coach name is required';
      const coachPhoneValidation = validatePhoneNumber(coachPhone);
      if (!coachPhoneValidation.isValid) {
        newErrors.coachPhone = coachPhoneValidation.message;
      }
      if (!clubName.trim()) newErrors.clubName = 'Club name is required';
    }
    
    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const validateStep5 = () => {
    const newErrors = {};
    
    // Step 5 for coach: League Name (optional, no validation needed)
    // This step is optional, so we don't need to validate anything
    
    setErrors(newErrors);
    return { isValid: true, errors: newErrors };
  };

  const validateStep6 = () => {
    const newErrors = {};
    
    // Step 6 for coach: Password and Confirm Password
    if (!password.trim()) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };


  const handleFieldChange = (field, value, setter) => {
    setter(value);
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    // Mark field as touched
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleUserTypeSelect = (type) => {
    setUserType(type);
    setStep(2);
    setDisplayStep(2);
  };

  const handleTeamAgeSelect = (value) => {
    setIsAcademy(value);
    setTouched(prev => ({ ...prev, isAcademy: true }));
    if (value === 'no') {
      setAgeGroup('');
    }
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.isAcademy;
      if (value === 'no') {
        delete newErrors.ageGroup;
      }
      return newErrors;
    });
  };

  const renderYouthAdultChoice = (question) => (
    <div className="form-group" id="isAcademy">
      <label>{question}</label>
      <div className="team-age-selection">
        <button
          type="button"
          className={`team-age-card ${isAcademy === 'yes' ? 'selected' : ''}`}
          onClick={() => handleTeamAgeSelect('yes')}
        >
          <span className="team-age-title">Youth team</span>
          <span className="team-age-subtext">
            Children or teenagers. We'll invite parents so families can manage availability and stay in the loop.
          </span>
        </button>
        <button
          type="button"
          className={`team-age-card ${isAcademy === 'no' ? 'selected' : ''}`}
          onClick={() => handleTeamAgeSelect('no')}
        >
          <span className="team-age-title">Adult squad</span>
          <span className="team-age-subtext">
            Open-age Saturday or Sunday league. Players manage their own availability — no parent accounts.
          </span>
        </button>
      </div>
      {touched.isAcademy && errors.isAcademy && (
        <span className="error-message">{errors.isAcademy}</span>
      )}
    </div>
  );

  const handleNext = async () => {
    if (step === 2) {
      // Mark all step 2 fields as touched
      if (userType === 'parent') {
        setTouched(prev => ({
          ...prev,
          firstName: true,
          lastName: true,
          mobile: true
        }));
      } else {
        setTouched(prev => ({
          ...prev,
          firstName: true,
          lastName: true,
          mobile: true
        }));
      }
      
      // Validate and proceed if valid
      const validation = validateStep2();
      
      if (!validation.isValid) {
        // Scroll to first error
        const firstErrorField = Object.keys(validation.errors)[0];
        if (firstErrorField) {
          setTimeout(() => {
            const element = document.getElementById(firstErrorField);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              setTimeout(() => element.focus(), 300);
            }
          }, 100);
        }
        return;
      }
      
      setSubmitMessage('');
      setUserExists(false);
      setUserExistsMessage('');
      setCanContinue(false);
      
      // Check if user already exists
      const exists = await checkUserExists();
      
      // If user exists and can't continue, stop here
      if (exists && !canContinue) {
        // Don't send partial sign-up email if user exists
        return;
      }
      
      // Only send partial sign-up notification email if user doesn't exist
      if (!exists) {
        try {
          await api.post('/partial_signup_notification.php', {
            userType,
            firstName,
            lastName,
            mobile
          });
        } catch (error) {
          // Log error but don't block the flow
          console.error('Failed to send partial sign-up notification:', error);
        }
      }
      
      // Clear submit message and user exists message when moving past step 2
      setSubmitMessage('');
      setSubmitSuccess(false);
      setUserExistsMessage('');
      
      // For players, go to step 3 (club type question), for others go to step 3 (their respective step)
      if (userType === 'player') {
        setStep(3);
        setTimeout(() => {
          setDisplayStep(3);
        }, 100);
      } else {
        setStep(3);
        setTimeout(() => {
          setDisplayStep(3);
        }, 100);
      }
    } else if (step === 3) {
      // Step 3: Club Type Question (player) or Club Name (coach) or Child Details (parent)
      if (userType === 'player') {
        // Step 3 for player: Team Type Question
        setTouched(prev => ({
          ...prev,
          playerClubType: true
        }));
        
        if (!playerClubType) {
          setErrors(prev => ({
            ...prev,
            playerClubType: 'Please select a team type'
          }));
          return;
        }
        
        setSubmitMessage('');
        setUserExistsMessage('');
        // Both organizer and team go to step 4 (Team Name for organizer, Team Information for team)
        setStep(4);
        setTimeout(() => {
          setDisplayStep(4);
        }, 100);
      } else if (userType === 'parent') {
        // Mark all step 3 fields as touched for parents
        setTouched(prev => ({
          ...prev,
          childFirstName: true,
          childLastName: true
        }));
        
        // Validate step 3 for parents
        const validation = validateStep3();
        
        if (!validation.isValid) {
          const firstErrorField = Object.keys(validation.errors)[0];
          if (firstErrorField) {
            setTimeout(() => {
              const element = document.getElementById(firstErrorField);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setTimeout(() => element.focus(), 300);
              }
            }, 100);
          }
          return;
        }
        
        setSubmitMessage('');
        setUserExistsMessage('');
        setStep(4); // Go to password step
        setTimeout(() => {
          setDisplayStep(4);
        }, 100);
      } else if (userType === 'coach') {
        // Step 3 for coach: Club Name
        setTouched(prev => ({
          ...prev,
          clubName: true
        }));
        
        const validation = validateStep3();
        
        if (!validation.isValid) {
          const firstErrorField = Object.keys(validation.errors)[0];
          if (firstErrorField) {
            setTimeout(() => {
              const element = document.getElementById(firstErrorField);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setTimeout(() => element.focus(), 300);
              }
            }, 100);
          }
          return;
        }
        
        setSubmitMessage('');
        setUserExistsMessage('');
        setStep(4);
        // Delay breadcrumb animation
        setTimeout(() => {
          setDisplayStep(4);
        }, 100);
      }
    } else if (step === 4) {
      // Step 4: Different for each user type
      if (userType === 'player' && playerClubType === 'organizer') {
        // Step 4 for organizer: Team Name
        setTouched(prev => ({
          ...prev,
          clubName: true
        }));
        
        const validation = validateStep4();
        if (!validation.isValid) {
          const firstErrorField = Object.keys(validation.errors)[0];
          if (firstErrorField) {
            setTimeout(() => {
              const element = document.getElementById(firstErrorField);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setTimeout(() => element.focus(), 300);
              }
            }, 100);
          }
          return;
        }
        
        setSubmitMessage('');
        // If player exists, skip password step and go directly to submission
        if (userExists && canContinue) {
          // For existing players, Step 4 is the final step - trigger form submission
          // The form's onSubmit handler will call handleSubmit
          const form = document.querySelector('form');
          if (form) {
            const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
            form.dispatchEvent(submitEvent);
          }
        } else {
          setStep(5); // Go to password step
          setTimeout(() => {
            setDisplayStep(5);
          }, 100);
        }
      } else if (userType === 'player' && playerClubType === 'team') {
        // Step 4 for team player: Team Information
        setTouched(prev => ({
          ...prev,
          clubName: true,
          isAcademy: true,
          ageGroup: true,
          coachName: true,
          coachPhone: true
        }));
        
        const validation = validateStep4();
        if (!validation.isValid) {
          const firstErrorField = Object.keys(validation.errors)[0];
          if (firstErrorField) {
            setTimeout(() => {
              const element = document.getElementById(firstErrorField);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setTimeout(() => element.focus(), 300);
              }
            }, 100);
          }
          return;
        }
        
        setSubmitMessage('');
        // For team players, Step 4 is the final step (no password needed)
        // If player exists, skip password step and submit directly
        if (userExists && canContinue || playerClubType === 'team') {
          // Create a synthetic submit event and call handleSubmit
          const syntheticEvent = { preventDefault: () => {} };
          handleSubmit(syntheticEvent);
        } else {
          setStep(5); // Go to password step (only for organizer players)
          setTimeout(() => {
            setDisplayStep(5);
          }, 100);
        }
      } else if (userType === 'parent') {
        // Step 4 for parent: Club Details (final step - no password needed)
        setTouched(prev => ({
          ...prev,
          coachName: true,
          coachPhone: true,
          clubName: true
        }));
        
        const validation = validateStep4();
        if (!validation.isValid) {
          const firstErrorField = Object.keys(validation.errors)[0];
          if (firstErrorField) {
            setTimeout(() => {
              const element = document.getElementById(firstErrorField);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setTimeout(() => element.focus(), 300);
              }
            }, 100);
          }
          return;
        }
        
        setSubmitMessage('');
        // Step 4 is final step for parents - submit directly
        const syntheticEvent = { preventDefault: () => {} };
        handleSubmit(syntheticEvent);
      } else if (userType === 'coach') {
        // Step 4 for coach: Academy and Age Group
        setTouched(prev => ({
          ...prev,
          isAcademy: true,
          ageGroup: true
        }));
        
        const validation = validateStep4();
        
        if (!validation.isValid) {
          const firstErrorField = Object.keys(validation.errors)[0];
          if (firstErrorField) {
            setTimeout(() => {
              const element = document.getElementById(firstErrorField);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setTimeout(() => element.focus(), 300);
              }
            }, 100);
          }
          return;
        }
        
        setSubmitMessage('');
        setStep(5);
        // Delay breadcrumb animation
        setTimeout(() => {
          setDisplayStep(5);
        }, 100);
      }
    } else if (step === 5) {
      // Step 5: Password for organizer players; League Name for coaches
      // Note: Team players and parents don't have Step 5 - they submit after Step 4
      if (userType === 'player' && playerClubType === 'organizer') {
        // Step 5 for organizer: Password (final step, submit happens in handleSubmit)
        setTouched(prev => ({
          ...prev,
          password: true,
          confirmPassword: true
        }));
        
        const validation = validateStep6();
        if (!validation.isValid) {
          const firstErrorField = Object.keys(validation.errors)[0];
          if (firstErrorField) {
            setTimeout(() => {
              const element = document.getElementById(firstErrorField);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setTimeout(() => element.focus(), 300);
              }
            }, 100);
          }
          return;
        }
        // Password step is final for organizers, submit happens in handleSubmit
      } else if (userType === 'coach') {
        // Step 5 for coach: League Name (optional, no validation)
        // If coach exists and can continue, submit directly (skip password step)
        if (userExists && canContinue) {
          const syntheticEvent = { preventDefault: () => {} };
          handleSubmit(syntheticEvent);
        } else {
          setSubmitMessage('');
          setStep(6);
          // Delay breadcrumb animation
          setTimeout(() => {
            setDisplayStep(6);
          }, 100);
        }
      }
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setUserType('');
      setDisplayStep(1);
    } else if (step === 3) {
      setStep(2);
      setDisplayStep(2);
    } else if (step === 4) {
      if (userType === 'player' && playerClubType === 'organizer') {
        setStep(3);
        setDisplayStep(3);
      } else if (userType === 'player' && playerClubType === 'team') {
        setStep(3);
        setDisplayStep(3);
      } else if (userType === 'parent') {
        setStep(3);
        setDisplayStep(3);
      } else if (userType === 'coach') {
        setStep(3);
        setDisplayStep(3);
      }
    } else if (step === 5) {
      // Step 5 only exists for organizer players and coaches
      if (userType === 'player' && playerClubType === 'organizer') {
        setStep(4);
        setDisplayStep(4);
      } else if (userType === 'coach') {
        setStep(4);
        setDisplayStep(4);
      }
    } else if (step === 6) {
      setStep(5);
      setDisplayStep(5);
    }
  };

  const checkUserExists = async (checkClubName = '') => {
    try {
      const response = await api.post('/check_user_exists.php', {
        userType,
        mobile,
        clubName: checkClubName || clubName || '', // Use provided clubName or current state
        playerClubType: userType === 'player' ? playerClubType : null
      });

      if (response.data.success) {
        if (response.data.exists) {
          setUserExists(true);
          // Store existing teams from API response
          setExistingTeams(response.data.existingTeams || []);
          
          // Handle different user types
          if (userType === 'parent') {
            setUserExistsMessage('An account with this mobile number already exists. If you are registering another child, please continue. Otherwise log-in or reset your password.');
            setCanContinue(true);
          } else if (userType === 'coach') {
            // Build message with existing teams list
            let message = 'An account with this mobile number already exists. ';
            
            // Add existing teams information
            if (response.data.existingTeams && response.data.existingTeams.length > 0) {
              const teamNames = response.data.existingTeams.map(team => team.team_name).join(', ');
              message += `You are already associated with: ${teamNames}. `;
            }
            
            // Check if we have clubName to verify team doesn't exist
            if (checkClubName || clubName) {
              if (response.data.canAddNewTeam) {
                message += 'If you are creating a new team, please continue. Otherwise log-in or reset your password.';
                setUserExistsMessage(message);
                setCanContinue(true);
              } else {
                message += 'You are already registered for this team. Please log-in or reset your password instead.';
                setUserExistsMessage(message);
                setCanContinue(false);
              }
            } else {
              // At step 2, we don't know the team name yet, so allow continue
              // Team name check will happen at final submission
              message += 'If you are creating a new team, please continue. Otherwise log-in or reset your password.';
              setUserExistsMessage(message);
              setCanContinue(true);
            }
          } else if (userType === 'player') {
            // Build message with existing teams list
            let message = 'An account with this mobile number already exists. ';
            
            // Add existing teams information
            if (response.data.existingTeams && response.data.existingTeams.length > 0) {
              const teamNames = response.data.existingTeams.map(team => team.team_name).join(', ');
              message += `You are already associated with: ${teamNames}. `;
            }
            
            // Check if we have clubName to verify team doesn't exist
            if (checkClubName || clubName) {
              if (response.data.canAddNewTeam) {
                message += 'If you are creating a new team, please continue. Otherwise log-in or reset your password.';
                setUserExistsMessage(message);
                setCanContinue(true);
              } else {
                message += 'You are already registered for this team. Please log-in or reset your password instead.';
                setUserExistsMessage(message);
                setCanContinue(false);
              }
            } else {
              // At step 2, we don't know the team name yet, so allow continue
              // Team name check will happen at final submission
              message += 'If you are creating a new team, please continue. Otherwise log-in or reset your password.';
              setUserExistsMessage(message);
              setCanContinue(true);
            }
          }
          return true; // User exists
        } else {
          setUserExists(false);
          setUserExistsMessage('');
          setCanContinue(false);
          setExistingTeams([]);
          return false; // User doesn't exist
        }
      }
      return false;
    } catch (error) {
      console.error('Check user exists error:', error);
      // If check fails, allow registration to proceed (fail open)
      setUserExists(false);
      setUserExistsMessage('');
      setCanContinue(false);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Ensure we're on the final step before submitting
    const finalStep = getTotalSteps();
    if (step !== finalStep) {
      // If not on final step, just proceed to next step instead
      handleNext();
      return;
    }
    
    // Validate mobile is present
    if (!mobile || !mobile.trim()) {
      setSubmitMessage('Mobile number is required');
      setSubmitSuccess(false);
      return;
    }
    
    // Mark all final step fields as touched and validate
    let validation;
    
    if (userType === 'parent') {
      // Parents don't need password validation - Step 4 is their final step
      validation = { isValid: true, errors: {} };
    } else if (userType === 'coach') {
      // For existing coaches, skip password validation
      if (userExists && canContinue) {
        validation = { isValid: true, errors: {} };
      } else {
        // Step 6 for coach: Password
        setTouched(prev => ({
          ...prev,
          password: true,
          confirmPassword: true
        }));
        
        validation = validateStep6();
      }
    } else if (userType === 'player') {
      // For existing players or team players (non-organizers), skip password validation
      if (userExists && canContinue || playerClubType === 'team') {
        validation = { isValid: true, errors: {} };
      } else {
        // Player: Step 5 for organizer players only - password step
        setTouched(prev => ({
          ...prev,
          password: true,
          confirmPassword: true
        }));
        
        validation = validateStep6();
      }
    } else {
      // Fallback
      validation = { isValid: true, errors: {} };
    }
    
    if (!validation.isValid) {
      const firstErrorField = Object.keys(validation.errors)[0];
      if (firstErrorField) {
        setTimeout(() => {
          const element = document.getElementById(firstErrorField);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => element.focus(), 300);
          }
        }, 100);
      }
      return;
    }

    // For existing users, we already checked in Step 2
    // Only check again if we need to verify the team name doesn't exist
    if (userExists && canContinue) {
      // User exists and can continue - verify team name doesn't exist
      const teamCheck = await checkUserExists(clubName);
      if (teamCheck && !canContinue) {
        // Team name already exists, can't continue
        setIsSubmitting(false);
        return;
      }
      // If canContinue is still true, proceed with submission
    } else if (!userExists) {
      // New user - check if they exist (shouldn't happen at this point, but safety check)
      const exists = await checkUserExists(clubName);
      if (exists && !canContinue) {
        setIsSubmitting(false);
        return;
      }
    } else {
      // User exists but can't continue - shouldn't reach here, but stop anyway
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitSuccess(false);

    try {
      const response = await api.post('/register_user.php', {
        userType,
        firstName,
        lastName,
        mobile,
        clubName,
        isAcademy,
        ageGroup,
        // Player-specific fields
        playerClubType: userType === 'player' ? playerClubType : null,
        // For existing coaches, existing players, team players (non-organizers), and parents, don't send password
        password: ((userType === 'coach' && !(userExists && canContinue)) || (userType === 'player' && playerClubType === 'organizer' && !(userExists && canContinue))) ? password : null,
        // Coach-specific fields
        leagueName: userType === 'coach' ? leagueName : null,
        // Parent-specific fields
        childFirstName: userType === 'parent' ? childFirstName : null,
        childLastName: userType === 'parent' ? childLastName : null,
        // Manager/Coach details for parents and team players
        coachName: (userType === 'parent' || (userType === 'player' && playerClubType === 'team')) ? coachName : null,
        coachPhone: (userType === 'parent' || (userType === 'player' && playerClubType === 'team')) ? coachPhone : null,
        // Flag to indicate existing user adding new team
        existingUser: userExists && canContinue
      });

      if (response.data.success) {
        // Different success messages based on user type
        if (userType === 'coach') {
          setSubmitMessage('Registration successful! Your team has been created. You can now log in using your mobile number and password.');
        } else if (userType === 'player' && playerClubType === 'organizer') {
          setSubmitMessage('Registration successful! Your team has been created. You can now log in using your mobile number and password.');
        } else {
          setSubmitMessage('Registration submitted successfully! We will be in touch soon.');
        }
        setSubmitSuccess(true);
        setTimeout(() => {
          onClose();
        }, 6000); // Increased from 2000ms to 6000ms (6 seconds)
      } else {
        setSubmitMessage(response.data.message || 'Registration failed. Please try again.');
        setSubmitSuccess(false);
      }
    } catch (error) {
      console.error('Registration error:', error);
      setSubmitSuccess(false);
      // Display API error message if available
      if (error.response && error.response.data && error.response.data.message) {
        setSubmitMessage(error.response.data.message);
      } else {
        setSubmitMessage('An error occurred. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getTotalSteps = () => {
    if (userType === 'parent') return 4; // No password step
    // For coaches, if account exists, skip password step (5 steps instead of 6)
    if (userType === 'coach' && userExists && canContinue) return 5;
    if (userType === 'coach') return 6;
    // For players, if account exists, skip password step (4 steps instead of 5)
    if (userType === 'player' && userExists && canContinue) return 4;
    // For players, if they organize matches, they have 5 steps (Club Name + password)
    if (userType === 'player' && playerClubType === 'organizer') return 5;
    // For team players (non-organizers), no password step - 4 steps total
    if (userType === 'player' && playerClubType === 'team') return 4;
    if (userType === 'player') return 5; // fallback
    return 3; // fallback
  };

  const getStepIcon = () => {
    if (step === 1) return null;
    if (userType === 'player') return <FaUser className="step-icon" />;
    if (userType === 'coach') return <FaUserTie className="step-icon" />;
    if (userType === 'parent') return <FaBaby className="step-icon" />;
    return null;
  };

  const renderStepIndicator = () => {
    const totalSteps = getTotalSteps();
    // Use displayStep for breadcrumb animation, but step for form content
    const indicatorStep = displayStep;
    return (
      <div className="step-indicator">
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((stepNum) => (
          <React.Fragment key={stepNum}>
            <div className={`step-dot ${stepNum === indicatorStep ? 'active' : stepNum < indicatorStep ? 'completed' : ''}`}>
              {stepNum < indicatorStep ? '✓' : stepNum}
            </div>
            {stepNum < totalSteps && (
              <div className={`step-line ${stepNum < indicatorStep ? 'completed' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderStep1 = () => (
    <div className="register-step step-1">
      <h2>I am a...</h2>
      <div className="user-type-selection">
        <button
          type="button"
          className={`user-type-card ${userType === 'player' ? 'selected' : ''}`}
          onClick={() => handleUserTypeSelect('player')}
        >
          <FaUser className="user-type-icon" />
          <span>Player</span>
          <p className="user-type-subtext">
            Join a youth team or adult squad, or organise weekly friendly matches
          </p>
        </button>
        <button
          type="button"
          className={`user-type-card ${userType === 'coach' ? 'selected' : ''}`}
          onClick={() => handleUserTypeSelect('coach')}
        >
          <FaUserTie className="user-type-icon" />
          <span>Coach / Manager</span>
          <p className="user-type-subtext">
            I coach a youth team (with parents) or an adult Saturday / Sunday league squad
          </p>
        </button>
        <button
          type="button"
          className={`user-type-card ${userType === 'parent' ? 'selected' : ''}`}
          onClick={() => handleUserTypeSelect('parent')}
        >
          <FaBaby className="user-type-icon" />
          <span>Parent</span>
          <p className="user-type-subtext">
            My child plays for a youth team
          </p>
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="register-step step-2">
      <div className="step-header">
        {getStepIcon()}
        {renderStepIndicator()}
      </div>
      <h2>Your Details</h2>
      
      {userType === 'parent' ? (
        <>
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => handleFieldChange('firstName', e.target.value, setFirstName)}
              onBlur={() => setTouched(prev => ({ ...prev, firstName: true }))}
              className={touched.firstName && errors.firstName ? 'error' : ''}
              required
              placeholder="Enter your first name"
            />
            {touched.firstName && errors.firstName && (
              <span className="error-message">{errors.firstName}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => handleFieldChange('lastName', e.target.value, setLastName)}
              onBlur={() => setTouched(prev => ({ ...prev, lastName: true }))}
              className={touched.lastName && errors.lastName ? 'error' : ''}
              required
              placeholder="Enter your last name"
            />
            {touched.lastName && errors.lastName && (
              <span className="error-message">{errors.lastName}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="mobile">Mobile</label>
            <input
              type="tel"
              id="mobile"
              value={mobile}
              onChange={(e) => handleFieldChange('mobile', e.target.value, setMobile)}
              onBlur={() => setTouched(prev => ({ ...prev, mobile: true }))}
              className={touched.mobile && errors.mobile ? 'error' : ''}
              required
              placeholder="Enter your mobile number"
            />
            {touched.mobile && errors.mobile && (
              <span className="error-message">{errors.mobile}</span>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => handleFieldChange('firstName', e.target.value, setFirstName)}
              onBlur={() => setTouched(prev => ({ ...prev, firstName: true }))}
              className={touched.firstName && errors.firstName ? 'error' : ''}
              required
              placeholder="Enter your first name"
            />
            {touched.firstName && errors.firstName && (
              <span className="error-message">{errors.firstName}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => handleFieldChange('lastName', e.target.value, setLastName)}
              onBlur={() => setTouched(prev => ({ ...prev, lastName: true }))}
              className={touched.lastName && errors.lastName ? 'error' : ''}
              required
              placeholder="Enter your last name"
            />
            {touched.lastName && errors.lastName && (
              <span className="error-message">{errors.lastName}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="mobile">Mobile</label>
            <input
              type="tel"
              id="mobile"
              value={mobile}
              onChange={(e) => handleFieldChange('mobile', e.target.value, setMobile)}
              onBlur={() => setTouched(prev => ({ ...prev, mobile: true }))}
              className={touched.mobile && errors.mobile ? 'error' : ''}
              required
              placeholder="Enter your mobile number"
            />
            {touched.mobile && errors.mobile && (
              <span className="error-message">{errors.mobile}</span>
            )}
          </div>
        </>
      )}

      {userExists && userExistsMessage && (
        <div className={`submit-message ${canContinue ? 'warning' : 'error'}`}>
          <p>
            {userExistsMessage.includes('log-in') ? (
              <>
                {userExistsMessage.split('log-in')[0]}
                <a href="/" onClick={(e) => { e.preventDefault(); onClose(); window.location.href = '/'; }}>
                  log-in
                </a>
                {userExistsMessage.split('log-in')[1]?.includes('reset') ? (
                  <>
                    {' '}or{' '}
                    <a href="/forgot-password" onClick={(e) => { e.preventDefault(); onClose(); window.location.href = '/forgot-password'; }}>
                      reset your password
                    </a>.
                  </>
                ) : userExistsMessage.split('log-in')[1]}
              </>
            ) : (
              userExistsMessage
            )}
          </p>
        </div>
      )}

      <div className="step-actions">
        <button type="button" className="btn-next" onClick={handleNext} disabled={userExists && !canContinue}>
          Next <FaChevronRight />
        </button>
        <button type="button" className="btn-back" onClick={handleBack}>
          <FaChevronLeft /> Back
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => {
    if (userType === 'parent') {
      return (
        <div className="register-step step-3">
          <div className="step-header">
            {getStepIcon()}
            {renderStepIndicator()}
          </div>
          <h2>Your Child Details</h2>
          
          <div className="form-group">
            <label htmlFor="childFirstName">Child's First Name</label>
            <input
              type="text"
              id="childFirstName"
              value={childFirstName}
              onChange={(e) => handleFieldChange('childFirstName', e.target.value, setChildFirstName)}
              onBlur={() => setTouched(prev => ({ ...prev, childFirstName: true }))}
              className={touched.childFirstName && errors.childFirstName ? 'error' : ''}
              required
              placeholder="Enter child's first name"
            />
            {touched.childFirstName && errors.childFirstName && (
              <span className="error-message">{errors.childFirstName}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="childLastName">Child's Last Name</label>
            <input
              type="text"
              id="childLastName"
              value={childLastName}
              onChange={(e) => handleFieldChange('childLastName', e.target.value, setChildLastName)}
              onBlur={() => setTouched(prev => ({ ...prev, childLastName: true }))}
              className={touched.childLastName && errors.childLastName ? 'error' : ''}
              required
              placeholder="Enter child's last name"
            />
            {touched.childLastName && errors.childLastName && (
              <span className="error-message">{errors.childLastName}</span>
            )}
          </div>

          <div className="step-actions">
            <button type="button" className="btn-next" onClick={handleNext}>
              Next <FaChevronRight />
            </button>
            <button type="button" className="btn-back" onClick={handleBack}>
              <FaChevronLeft /> Back
            </button>
          </div>
        </div>
      );
    }

    // For coaches, Step 3 is just Club Name
    if (userType === 'coach') {
      return (
        <div className="register-step step-3">
          <div className="step-header">
            {getStepIcon()}
            {renderStepIndicator()}
          </div>
          <h2>Club Name</h2>
          
          <div className="form-group">
            <label htmlFor="clubName">My Club Name</label>
            <input
              type="text"
              id="clubName"
              value={clubName}
              onChange={(e) => handleFieldChange('clubName', e.target.value, setClubName)}
              onBlur={() => setTouched(prev => ({ ...prev, clubName: true }))}
              className={touched.clubName && errors.clubName ? 'error' : ''}
              required
              placeholder="Enter your club name"
            />
            {touched.clubName && errors.clubName && (
              <span className="error-message">{errors.clubName}</span>
            )}
          </div>

          <div className="step-actions">
            <button type="button" className="btn-next" onClick={handleNext}>
              Next <FaChevronRight />
            </button>
            <button type="button" className="btn-back" onClick={handleBack}>
              <FaChevronLeft /> Back
            </button>
          </div>
        </div>
      );
    }

    // For players, Step 3 is Team Type Question
    return (
      <div className="register-step step-3">
        <div className="step-header">
          {getStepIcon()}
          {renderStepIndicator()}
        </div>
        <h2>Team Type</h2>
        
        <div className="form-group">
          <label>What type of team are you part of?</label>
          <div className="radio-group">
            <label className={`radio-option ${playerClubType === 'organizer' ? 'checked' : ''}`}>
              <input
                type="radio"
                name="playerClubType"
                value="organizer"
                checked={playerClubType === 'organizer'}
                onChange={(e) => {
                  setPlayerClubType(e.target.value);
                  if (errors.playerClubType) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.playerClubType;
                      return newErrors;
                    });
                  }
                }}
              />
              <span>I organise matches (e.g. 5-a-side)</span>
            </label>
            <label className={`radio-option ${playerClubType === 'team' ? 'checked' : ''}`}>
              <input
                type="radio"
                name="playerClubType"
                value="team"
                checked={playerClubType === 'team'}
                onChange={(e) => {
                  setPlayerClubType(e.target.value);
                  if (errors.playerClubType) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.playerClubType;
                      return newErrors;
                    });
                  }
                }}
              />
              <span>I'm part of a club or<br/>Saturday / Sunday League team</span>
            </label>
          </div>
          {touched.playerClubType && errors.playerClubType && (
            <span className="error-message">{errors.playerClubType}</span>
          )}
        </div>

        <div className="step-actions">
          <button type="button" className="btn-next" onClick={handleNext}>
            Next <FaChevronRight />
          </button>
          <button type="button" className="btn-back" onClick={handleBack}>
            <FaChevronLeft /> Back
          </button>
        </div>
      </div>
    );
  };

  const renderStep4 = () => {
    // For players who organize matches, Step 4 is Team Name
    if (userType === 'player' && playerClubType === 'organizer') {
      return (
        <div className="register-step step-4">
          <div className="step-header">
            {getStepIcon()}
            {renderStepIndicator()}
          </div>
          <h2>Team Name</h2>
          
          <form onSubmit={(e) => { 
            e.preventDefault(); 
            // For existing players, Step 4 is the final step - submit directly
            if (userExists && canContinue) {
              handleSubmit(e);
            } else {
              handleNext();
            }
          }}>
            <div className="form-group">
              <label htmlFor="clubName">My Team Name</label>
              <input
                type="text"
                id="clubName"
                value={clubName}
                onChange={(e) => handleFieldChange('clubName', e.target.value, setClubName)}
                onBlur={() => setTouched(prev => ({ ...prev, clubName: true }))}
                className={touched.clubName && errors.clubName ? 'error' : ''}
                required
                placeholder="Enter your team name"
              />
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.6)', 
                fontSize: '0.875rem', 
                marginTop: '0.5rem',
                marginBottom: 0
              }}>
                If you don't have a team name, please create one. We use this to associate your players to the team.
              </p>
              {touched.clubName && errors.clubName && (
                <span className="error-message">{errors.clubName}</span>
              )}
            </div>

            {step === 2 && userExists && userExistsMessage && (
              <div className={`submit-message ${canContinue ? 'warning' : 'error'}`}>
                <p>
                  {userExistsMessage.includes('log-in') ? (
                    <>
                      {userExistsMessage.split('log-in')[0]}
                      <a href="/" onClick={(e) => { e.preventDefault(); onClose(); window.location.href = '/'; }}>
                        log-in
                      </a>
                      {userExistsMessage.split('log-in')[1]?.includes('reset') ? (
                        <>
                          {' '}or{' '}
                          <a href="/forgot-password" onClick={(e) => { e.preventDefault(); onClose(); window.location.href = '/forgot-password'; }}>
                            reset your password
                          </a>.
                        </>
                      ) : userExistsMessage.split('log-in')[1]}
                    </>
                  ) : (
                    userExistsMessage
                  )}
                </p>
              </div>
            )}

            {submitMessage && (
              <div className={`submit-message ${submitSuccess ? 'success' : 'error'}`}>
                <p>{submitMessage}</p>
              </div>
            )}

            <div className="step-actions">
              <button 
                type="submit"
                className={userExists && canContinue ? 'btn-submit' : 'btn-next'} 
                disabled={isSubmitting || (userExists && !canContinue)}
              >
                {isSubmitting ? 'Submitting...' : (userExists && canContinue ? 'Submit' : 'Next')} 
                {!(userExists && canContinue) && <FaChevronRight />}
              </button>
              <button type="button" className="btn-back" onClick={handleBack}>
                <FaChevronLeft /> Back
              </button>
            </div>
          </form>
        </div>
      );
    }

    // For players who are part of a team, Step 4 is Team Information
    if (userType === 'player' && playerClubType === 'team') {
      return (
        <div className="register-step step-4">
          <div className="step-header">
            {getStepIcon()}
            {renderStepIndicator()}
          </div>
          <h2>Team Information</h2>
          
          <form onSubmit={(e) => { 
            e.preventDefault(); 
            // For existing players, Step 4 is the final step - submit directly
            if (userExists && canContinue) {
              handleSubmit(e);
            } else {
              handleNext();
            }
          }}>
            <div className="form-group">
              <label htmlFor="clubName">My Team Name</label>
              <input
                type="text"
                id="clubName"
                value={clubName}
                onChange={(e) => handleFieldChange('clubName', e.target.value, setClubName)}
                onBlur={() => setTouched(prev => ({ ...prev, clubName: true }))}
                className={touched.clubName && errors.clubName ? 'error' : ''}
                required
                placeholder="Enter your team name"
              />
              {touched.clubName && errors.clubName && (
                <span className="error-message">{errors.clubName}</span>
              )}
            </div>

            {renderYouthAdultChoice('Is this a youth team or an adult squad?')}

            {isAcademy === 'yes' && (
              <div className="form-group">
                <label htmlFor="ageGroup">Age Group</label>
                <select
                  id="ageGroup"
                  value={ageGroup}
                  onChange={(e) => handleFieldChange('ageGroup', e.target.value, setAgeGroup)}
                  onBlur={() => setTouched(prev => ({ ...prev, ageGroup: true }))}
                  className={touched.ageGroup && errors.ageGroup ? 'error' : ''}
                  required
                >
                  <option value="">Select Age Group</option>
                  {ageGroups.map((age) => (
                    <option key={age} value={age}>
                      {age}
                    </option>
                  ))}
                </select>
                {touched.ageGroup && errors.ageGroup && (
                  <span className="error-message">{errors.ageGroup}</span>
                )}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="coachName">Manager/Coach Name</label>
              <input
                type="text"
                id="coachName"
                value={coachName}
                onChange={(e) => handleFieldChange('coachName', e.target.value, setCoachName)}
                onBlur={() => setTouched(prev => ({ ...prev, coachName: true }))}
                className={touched.coachName && errors.coachName ? 'error' : ''}
                required
                placeholder="Enter manager/coach name"
              />
              {touched.coachName && errors.coachName && (
                <span className="error-message">{errors.coachName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="coachPhone">Manager/Coach Phone Number</label>
              <input
                type="tel"
                id="coachPhone"
                value={coachPhone}
                onChange={(e) => handleFieldChange('coachPhone', e.target.value, setCoachPhone)}
                onBlur={() => setTouched(prev => ({ ...prev, coachPhone: true }))}
                className={touched.coachPhone && errors.coachPhone ? 'error' : ''}
                required
                placeholder="Enter manager/coach phone number"
              />
              {touched.coachPhone && errors.coachPhone && (
                <span className="error-message">{errors.coachPhone}</span>
              )}
            </div>

            {step === 2 && userExists && userExistsMessage && (
              <div className={`submit-message ${canContinue ? 'warning' : 'error'}`}>
                <p>
                  {userExistsMessage.includes('log-in') ? (
                    <>
                      {userExistsMessage.split('log-in')[0]}
                      <a href="/" onClick={(e) => { e.preventDefault(); onClose(); window.location.href = '/'; }}>
                        log-in
                      </a>
                      {userExistsMessage.split('log-in')[1]?.includes('reset') ? (
                        <>
                          {' '}or{' '}
                          <a href="/forgot-password" onClick={(e) => { e.preventDefault(); onClose(); window.location.href = '/forgot-password'; }}>
                            reset your password
                          </a>.
                        </>
                      ) : userExistsMessage.split('log-in')[1]}
                    </>
                  ) : (
                    userExistsMessage
                  )}
                </p>
              </div>
            )}

            {submitMessage && (
              <div className={`submit-message ${submitSuccess ? 'success' : 'error'}`}>
                <p>{submitMessage}</p>
              </div>
            )}

            <div className="step-actions">
              <button 
                type="submit"
                className="btn-submit" 
                disabled={isSubmitting || (userExists && !canContinue)}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
              <button type="button" className="btn-back" onClick={handleBack}>
                <FaChevronLeft /> Back
              </button>
            </div>
          </form>
        </div>
      );
    }

    // Step 4 for coach: Academy and Age Group
    if (userType === 'coach') {
      return (
        <div className="register-step step-4">
          <div className="step-header">
            {getStepIcon()}
            {renderStepIndicator()}
          </div>
          <h2>About your team</h2>

          {renderYouthAdultChoice('Do you coach a youth team or an adult squad?')}

          {isAcademy === 'yes' && (
            <div className="form-group">
              <label htmlFor="ageGroup">Age group</label>
              <select
                id="ageGroup"
                value={ageGroup}
                onChange={(e) => handleFieldChange('ageGroup', e.target.value, setAgeGroup)}
                onBlur={() => setTouched(prev => ({ ...prev, ageGroup: true }))}
                className={touched.ageGroup && errors.ageGroup ? 'error' : ''}
                required
              >
                <option value="">Select Age Group</option>
                {ageGroups.map((age) => (
                  <option key={age} value={age}>
                    {age}
                  </option>
                ))}
              </select>
              {touched.ageGroup && errors.ageGroup && (
                <span className="error-message">{errors.ageGroup}</span>
              )}
            </div>
          )}

          <div className="step-actions">
            <button type="button" className="btn-next" onClick={handleNext}>
              Next <FaChevronRight />
            </button>
            <button type="button" className="btn-back" onClick={handleBack}>
              <FaChevronLeft /> Back
            </button>
          </div>
        </div>
      );
    }

    // Step 4 for parent: Club Details
    return (
      <div className="register-step step-4">
        <div className="step-header">
          {getStepIcon()}
          {renderStepIndicator()}
        </div>
        <h2>Club Details</h2>
        
        <form onSubmit={(e) => { 
          e.preventDefault(); 
          // Step 4 is final step for parents - submit directly
          handleSubmit(e);
        }}>
          <div className="form-group">
            <label htmlFor="coachName">Coach Name</label>
            <input
              type="text"
              id="coachName"
              value={coachName}
              onChange={(e) => handleFieldChange('coachName', e.target.value, setCoachName)}
              onBlur={() => setTouched(prev => ({ ...prev, coachName: true }))}
              className={touched.coachName && errors.coachName ? 'error' : ''}
              required
              placeholder="Enter coach name"
            />
            {touched.coachName && errors.coachName && (
              <span className="error-message">{errors.coachName}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="coachPhone">Coach Phone Number</label>
            <input
              type="tel"
              id="coachPhone"
              value={coachPhone}
              onChange={(e) => handleFieldChange('coachPhone', e.target.value, setCoachPhone)}
              onBlur={() => setTouched(prev => ({ ...prev, coachPhone: true }))}
              className={touched.coachPhone && errors.coachPhone ? 'error' : ''}
              required
              placeholder="Enter coach phone number"
            />
            {touched.coachPhone && errors.coachPhone && (
              <span className="error-message">{errors.coachPhone}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="clubName">Club Name</label>
            <input
              type="text"
              id="clubName"
              value={clubName}
              onChange={(e) => handleFieldChange('clubName', e.target.value, setClubName)}
              onBlur={() => setTouched(prev => ({ ...prev, clubName: true }))}
              className={touched.clubName && errors.clubName ? 'error' : ''}
              required
              placeholder="Enter club name"
            />
            {touched.clubName && errors.clubName && (
              <span className="error-message">{errors.clubName}</span>
            )}
          </div>

          <div className="form-group" style={{ marginTop: '1rem', padding: '0 20px' }}>
            <p style={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              fontSize: '0.9rem', 
              lineHeight: '1.5',
              margin: 0,
              textAlign: 'center'
            }}>
              After submitting your details, we will forward this information to your coach.
            </p>
          </div>

          {submitMessage && (
            <div className={`submit-message ${submitSuccess ? 'success' : 'error'}`}>
              <p>{submitMessage}</p>
            </div>
          )}

          <div className="step-actions">
            <button type="submit" className="btn-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
            <button type="button" className="btn-back" onClick={handleBack}>
              <FaChevronLeft /> Back
            </button>
          </div>
        </form>
      </div>
    );
  };

  const renderStep5 = () => {
    // For players who organize matches, Step 5 is Password (final step)
    if (userType === 'player' && playerClubType === 'organizer') {
      return (
        <div className="register-step step-5">
          <div className="step-header">
            {getStepIcon()}
            {renderStepIndicator()}
          </div>
          <h2>Create Password</h2>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => handleFieldChange('password', e.target.value, setPassword)}
                onBlur={() => setTouched(prev => ({ ...prev, password: true }))}
                className={touched.password && errors.password ? 'error' : ''}
                required
                placeholder="Enter your password (min 8 characters)"
              />
              {touched.password && errors.password && (
                <span className="error-message">{errors.password}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => handleFieldChange('confirmPassword', e.target.value, setConfirmPassword)}
                onBlur={() => setTouched(prev => ({ ...prev, confirmPassword: true }))}
                className={touched.confirmPassword && errors.confirmPassword ? 'error' : ''}
                required
                placeholder="Confirm your password"
              />
              {touched.confirmPassword && errors.confirmPassword && (
                <span className="error-message">{errors.confirmPassword}</span>
              )}
            </div>

            {userExists && userExistsMessage && (
              <div className={`submit-message ${canContinue ? 'warning' : 'error'}`}>
                <p>{userExistsMessage}</p>
                {!canContinue && (
                  <p style={{ marginTop: '10px' }}>
                    <a href="/" onClick={(e) => { e.preventDefault(); onClose(); window.location.href = '/'; }}>
                      Go to Login Page
                    </a>
                  </p>
                )}
              </div>
            )}

            {submitMessage && (
              <div className={`submit-message ${submitSuccess ? 'success' : 'error'}`}>
                <p>{submitMessage}</p>
              </div>
            )}

            <div className="step-actions">
              <button type="submit" className="btn-submit" disabled={isSubmitting || (userExists && !canContinue)}>
                {isSubmitting ? 'Submitting...' : 'Join'}
              </button>
              <button type="button" className="btn-back" onClick={handleBack}>
                <FaChevronLeft /> Back
              </button>
            </div>
          </form>
        </div>
      );
    }

    // Team players don't have a Step 5 - they submit after Step 4 (no password needed)
    // This step is only for organizer players

    // Parents don't have a Step 5 - they submit after Step 4 (no password needed)
    // This step is only for organizer players and coaches

    // For coaches, Step 5 is League Name
    return (
      <div className="register-step step-5">
        <div className="step-header">
          {getStepIcon()}
          {renderStepIndicator()}
        </div>
        <h2>League Name</h2>
        
        <form onSubmit={(e) => { 
          e.preventDefault(); 
          // For existing coaches, Step 5 is the final step - submit directly
          if (userExists && canContinue) {
            handleSubmit(e);
          } else {
            handleNext();
          }
        }}>
          <div className="form-group">
            <label htmlFor="leagueName">League Name</label>
            <input
              type="text"
              id="leagueName"
              value={leagueName}
              onChange={(e) => handleFieldChange('leagueName', e.target.value, setLeagueName)}
              onBlur={() => setTouched(prev => ({ ...prev, leagueName: true }))}
              className={touched.leagueName && errors.leagueName ? 'error' : ''}
              placeholder="Enter league name (optional)"
            />
            {touched.leagueName && errors.leagueName && (
              <span className="error-message">{errors.leagueName}</span>
            )}
          </div>

          {userExists && canContinue && (
            <div className="form-group" style={{ marginTop: '1rem', padding: '0 20px' }}>
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.7)', 
                fontSize: '0.9rem', 
                lineHeight: '1.5',
                margin: 0,
                textAlign: 'center'
              }}>
                After submitting, your team will be created. You can then log in and start adding players and parents.
              </p>
            </div>
          )}

          {submitMessage && (
            <div className={`submit-message ${submitSuccess ? 'success' : 'error'}`}>
              <p>{submitMessage}</p>
            </div>
          )}

          <div className="step-actions">
            <button 
              type="submit"
              className={userExists && canContinue ? 'btn-submit' : 'btn-next'} 
              disabled={isSubmitting || (userExists && !canContinue)}
            >
              {isSubmitting ? 'Submitting...' : (userExists && canContinue ? 'Submit' : 'Next')} 
              {!(userExists && canContinue) && <FaChevronRight />}
            </button>
            <button type="button" className="btn-back" onClick={handleBack}>
              <FaChevronLeft /> Back
            </button>
          </div>
        </form>
      </div>
    );
  };

  const renderStep6 = () => (
    <div className="register-step step-6">
      <div className="step-header">
        {getStepIcon()}
        {renderStepIndicator()}
      </div>
      <h2>Create Password</h2>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => handleFieldChange('password', e.target.value, setPassword)}
            onBlur={() => setTouched(prev => ({ ...prev, password: true }))}
            className={touched.password && errors.password ? 'error' : ''}
            required
            placeholder="Enter your password (min 8 characters)"
          />
          {touched.password && errors.password && (
            <span className="error-message">{errors.password}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password</label>
          <input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => handleFieldChange('confirmPassword', e.target.value, setConfirmPassword)}
            onBlur={() => setTouched(prev => ({ ...prev, confirmPassword: true }))}
            className={touched.confirmPassword && errors.confirmPassword ? 'error' : ''}
            required
            placeholder="Confirm your password"
          />
          {touched.confirmPassword && errors.confirmPassword && (
            <span className="error-message">{errors.confirmPassword}</span>
          )}
        </div>

        <div className="form-group" style={{ marginTop: '1rem', padding: '0 20px' }}>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.7)', 
            fontSize: '0.9rem', 
            lineHeight: '1.5',
            margin: 0,
            textAlign: 'center'
          }}>
            After submitting, your team will be created. You can then log in and start adding players and parents.
          </p>
        </div>

        {userExists && userExistsMessage && (
          <div className={`submit-message ${canContinue ? 'warning' : 'error'}`}>
            <p>{userExistsMessage}</p>
            {!canContinue && (
              <p style={{ marginTop: '10px' }}>
                <a href="/" onClick={(e) => { e.preventDefault(); onClose(); window.location.href = '/'; }} style={{ color: '#fff', textDecoration: 'underline' }}>
                  Go to Login Page
                </a>
              </p>
            )}
          </div>
        )}

        {submitMessage && (
          <div className={`submit-message ${submitSuccess ? 'success' : 'error'}`}>
            <p>{submitMessage}</p>
          </div>
        )}

        <div className="step-actions">
          <button type="submit" className="btn-submit" disabled={isSubmitting || (userExists && !canContinue)}>
            {isSubmitting ? 'Submitting...' : 'Join'}
          </button>
          <button type="button" className="btn-back" onClick={handleBack}>
            <FaChevronLeft /> Back
          </button>
        </div>
      </form>
    </div>
  );

  return createPortal(
    <div className="new-register-modal-overlay" onClick={handleOverlayClick}>
      <div className="new-register-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <button type="button" className="modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-content">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}
          {step === 6 && renderStep6()}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NewRegisterModal;

